import React from 'react';
import { ArrowLeft, Mail, Phone, MapPin, Clock } from 'lucide-react';
import { t } from '../services/i18n';
import { useSEOMetaTags } from './SEOMetaTags';

interface ContactPageProps {
  onBack: () => void;
}

export const ContactPage: React.FC<ContactPageProps> = ({ onBack }) => {
  useSEOMetaTags({
    title: 'Contact Us - Orcaseotools | Get Support & Assistance',
    description: 'Contact Orcaseotools for customer support, questions about our SEO and AEO audit tools, or business inquiries. Reach us at +1 (917) 795-8308 or visit our office in Albuquerque, New Mexico.',
    keywords: 'contact Orcaseotools, customer support, SEO tools support, website audit help, Orcaseotools contact, business inquiries',
    canonicalUrl: 'https://orcaseotools.com/contact',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact Us - Orcaseotools",
      "description": "Contact Orcaseotools for support and inquiries",
      "url": "https://orcaseotools.com/contact",
      "inLanguage": "en-US",
      "mainEntity": {
        "@type": "Organization",
        "name": "sofiahost LLC",
        "telephone": "+1-917-795-8308",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "1209 MOUNTAIN ROAD PL NE STE R",
          "addressLocality": "ALBUQUERQUE",
          "addressRegion": "NM",
          "postalCode": "87110",
          "addressCountry": "US"
        }
      }
    }
  });

  return (
    <div className="min-h-screen bg-[#0a0e27] text-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          {t('common.back') || 'Back'}
        </button>

        <article className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 space-y-6">
          <header>
            <h1 className="text-4xl font-bold mb-2">Contact Us</h1>
            <p className="text-slate-400 text-sm">We're here to help you with any questions or concerns</p>
          </header>

          <section className="space-y-6">
            <div className="bg-slate-900/50 rounded-lg p-6 space-y-4">
              <h2 className="text-2xl font-bold text-indigo-400 flex items-center gap-2">
                <MapPin size={24} />
                Company Information
              </h2>
              <div className="space-y-2 text-slate-300">
                <p><strong>Company Name:</strong> sofiahost LLC</p>
                <p><strong>Operating As:</strong> Orcaseotools</p>
                <p><strong>Entity ID:</strong> 4424925</p>
                <p><strong>Jurisdiction:</strong> New Mexico, United States</p>
                <p><strong>Registered Agent:</strong> Registered Agents Inc</p>
                <p className="pt-2"><strong>Address:</strong></p>
                <p className="ml-4">1209 MOUNTAIN ROAD PL NE STE R</p>
                <p className="ml-4">ALBUQUERQUE, NM, 87110, USA</p>
              </div>
            </div>

            <div className="bg-slate-900/50 rounded-lg p-6 space-y-4">
              <h2 className="text-2xl font-bold text-indigo-400 flex items-center gap-2">
                <Mail size={24} />
                Customer Support
              </h2>
              <p className="text-slate-300 leading-relaxed">
                Our dedicated customer support team at Orcaseotools is here to assist you with any inquiries or concerns you may have. Feel free to reach out to us via email for prompt assistance.
              </p>
              <p className="text-slate-300">
                <strong>Email:</strong> <a href="mailto:support@orcaseotools.com" className="text-indigo-400 hover:text-indigo-300">support@orcaseotools.com</a>
              </p>
              <p className="text-slate-300">
                <strong>Phone:</strong> <a href="tel:+19177958308" className="text-indigo-400 hover:text-indigo-300">+1 (917) 795-8308</a>
              </p>
            </div>

            <div className="bg-slate-900/50 rounded-lg p-6 space-y-4">
              <h2 className="text-2xl font-bold text-indigo-400 flex items-center gap-2">
                <Clock size={24} />
                Business Hours
              </h2>
              <p className="text-slate-300 leading-relaxed">
                Our customer support team is available to respond to your queries during the following hours:
              </p>
              <p className="text-slate-300">
                <strong>Monday to Friday:</strong> 9:00 AM - 5:00 PM (MST)
              </p>
              <p className="text-slate-300">
                <strong>Saturday & Sunday:</strong> Closed
              </p>
              <p className="text-slate-400 text-sm mt-2">
                Please note that response times may vary during peak periods.
              </p>
            </div>

            <div className="bg-slate-900/50 rounded-lg p-6 space-y-4">
              <h2 className="text-2xl font-bold text-indigo-400">Feedback</h2>
              <p className="text-slate-300 leading-relaxed">
                We value your feedback and suggestions to enhance your experience at Orcaseotools. If you have any comments or recommendations, please don't hesitate to share them with us at support@orcaseotools.com.
              </p>
            </div>
          </section>

          <div className="pt-6 border-t border-slate-700">
            <p className="text-slate-400 text-sm">
              Thank you for choosing <strong>Orcaseotools</strong>. We look forward to assisting you and providing excellent customer service.
            </p>
          </div>
        </article>
      </div>
    </div>
  );
};

